import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { MediaRequirementsEditor } from './MediaRequirementsEditor';
import { Camera, MessageSquare, CheckSquare, ChefHat, Package, Utensils, Refrigerator, Thermometer } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface TemplateItem {
  id: string;
  type: string;
  label: string;
  description?: string;
  required: boolean;
  supports_photos?: boolean;
  requires_photos?: boolean;
  supports_comments?: boolean;
  requires_comments?: boolean;
  min_photos?: number;
  max_photos?: number;
  [key: string]: any;
}

interface TemplateItemConfiguratorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: TemplateItem[];
  onSave: (updatedItems: TemplateItem[]) => void;
}

interface CategoryPreset {
  id: string;
  name: string;
  category: string;
  icon: any;
  description: string;
  requirements: {
    supports_photos: boolean;
    requires_photos: boolean;
    supports_comments: boolean;
    requires_comments: boolean;
    min_photos?: number;
    max_photos?: number;
  };
}

export const TemplateItemConfigurator: React.FC<TemplateItemConfiguratorProps> = ({
  open,
  onOpenChange,
  items,
  onSave,
}) => {
  const [configuredItems, setConfiguredItems] = useState<TemplateItem[]>(items);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string>('');

  // Define category-based presets
  const categoryPresets: CategoryPreset[] = useMemo(() => [
    {
      id: 'kitchen-full',
      name: 'Kitchen Area - Full Documentation',
      category: 'kitchen',
      icon: ChefHat,
      description: 'Photos and comments required for all kitchen items',
      requirements: {
        supports_photos: true,
        requires_photos: true,
        supports_comments: true,
        requires_comments: true,
        min_photos: 2,
        max_photos: 5,
      },
    },
    {
      id: 'kitchen-standard',
      name: 'Kitchen Area - Standard',
      category: 'kitchen',
      icon: ChefHat,
      description: 'Optional photos, required comments',
      requirements: {
        supports_photos: true,
        requires_photos: false,
        supports_comments: true,
        requires_comments: true,
        min_photos: 1,
        max_photos: 3,
      },
    },
    {
      id: 'storage-full',
      name: 'Storage Area - Full Documentation',
      category: 'storage',
      icon: Package,
      description: 'Photos and comments required for storage areas',
      requirements: {
        supports_photos: true,
        requires_photos: true,
        supports_comments: true,
        requires_comments: true,
        min_photos: 1,
        max_photos: 4,
      },
    },
    {
      id: 'storage-basic',
      name: 'Storage Area - Basic',
      category: 'storage',
      icon: Package,
      description: 'Comments required, photos optional',
      requirements: {
        supports_photos: true,
        requires_photos: false,
        supports_comments: true,
        requires_comments: true,
        min_photos: 1,
        max_photos: 2,
      },
    },
    {
      id: 'food-prep-full',
      name: 'Food Prep - Full Documentation',
      category: 'food_prep',
      icon: Utensils,
      description: 'Comprehensive documentation for food preparation',
      requirements: {
        supports_photos: true,
        requires_photos: true,
        supports_comments: true,
        requires_comments: true,
        min_photos: 2,
        max_photos: 6,
      },
    },
    {
      id: 'food-prep-standard',
      name: 'Food Prep - Standard',
      category: 'food_prep',
      icon: Utensils,
      description: 'Standard documentation for food prep',
      requirements: {
        supports_photos: true,
        requires_photos: false,
        supports_comments: true,
        requires_comments: true,
        min_photos: 1,
        max_photos: 3,
      },
    },
    {
      id: 'temperature-critical',
      name: 'Temperature Control - Critical',
      category: 'temperature',
      icon: Thermometer,
      description: 'Full documentation required for temperature logs',
      requirements: {
        supports_photos: true,
        requires_photos: true,
        supports_comments: true,
        requires_comments: true,
        min_photos: 1,
        max_photos: 3,
      },
    },
    {
      id: 'refrigeration-standard',
      name: 'Refrigeration - Standard',
      category: 'refrigeration',
      icon: Refrigerator,
      description: 'Standard requirements for refrigeration checks',
      requirements: {
        supports_photos: true,
        requires_photos: false,
        supports_comments: true,
        requires_comments: true,
        min_photos: 1,
        max_photos: 2,
      },
    },
  ], []);

  // Get unique categories from items
  const itemCategories = useMemo(() => {
    const categories = new Set(configuredItems.map(item => item.category).filter(Boolean));
    return Array.from(categories);
  }, [configuredItems]);

  // Sync state when dialog opens
  useEffect(() => {
    if (open) {
      setConfiguredItems(items);
      setSelectedItemIndex(items.length > 0 ? 0 : null);
    }
  }, [open, items]);

  const updateItemMedia = (index: number, updates: Partial<TemplateItem>) => {
    const newItems = [...configuredItems];
    newItems[index] = { ...newItems[index], ...updates };
    setConfiguredItems(newItems);
  };

  const applyToAll = (updates: Partial<TemplateItem>) => {
    const newItems = configuredItems.map(item => ({ ...item, ...updates }));
    setConfiguredItems(newItems);
  };

  const applyPresetToCategory = (presetId: string) => {
    const preset = categoryPresets.find(p => p.id === presetId);
    if (!preset) return;

    const newItems = configuredItems.map(item => {
      // Match items by category (case-insensitive)
      const itemCategory = (item.category || '').toLowerCase();
      if (itemCategory.includes(preset.category) || preset.category.includes(itemCategory)) {
        return { ...item, ...preset.requirements };
      }
      return item;
    });
    
    setConfiguredItems(newItems);
    setSelectedPreset('');
  };

  const handleSave = () => {
    onSave(configuredItems);
    onOpenChange(false);
  };

  const getItemRequirements = (item: TemplateItem) => {
    const badges = [];
    if (item.requires_photos) badges.push({ icon: Camera, text: 'Photo Required', color: 'destructive' });
    else if (item.supports_photos) badges.push({ icon: Camera, text: 'Photo Optional', color: 'secondary' });
    
    if (item.requires_comments) badges.push({ icon: MessageSquare, text: 'Comment Required', color: 'destructive' });
    else if (item.supports_comments) badges.push({ icon: MessageSquare, text: 'Comment Optional', color: 'secondary' });
    
    return badges;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Configure Evidence Requirements</DialogTitle>
          <DialogDescription>
            Set photo and comment requirements for each checklist item
          </DialogDescription>
        </DialogHeader>

        {/* Category-Based Presets */}
        {itemCategories.length > 0 && (
          <div className="border rounded-lg p-4 bg-muted/30 mb-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <ChefHat className="h-4 w-4" />
              Apply Category Presets
            </h3>
            <div className="flex gap-2">
              <Select value={selectedPreset} onValueChange={setSelectedPreset}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select a category preset..." />
                </SelectTrigger>
                <SelectContent>
                  {categoryPresets.map(preset => {
                    const Icon = preset.icon;
                    return (
                      <SelectItem key={preset.id} value={preset.id}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          <div>
                            <div className="font-medium">{preset.name}</div>
                            <div className="text-xs text-muted-foreground">{preset.description}</div>
                          </div>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Button 
                variant="secondary" 
                onClick={() => selectedPreset && applyPresetToCategory(selectedPreset)}
                disabled={!selectedPreset}
              >
                Apply Preset
              </Button>
            </div>
          </div>
        )}

        {/* Global Quick Actions */}
        <div className="flex gap-4 mb-4">
          <Button variant="outline" size="sm" onClick={() => applyToAll({
            supports_photos: false,
            requires_photos: false,
            supports_comments: false,
            requires_comments: false,
          })}>
            All Visual Only
          </Button>
          <Button variant="outline" size="sm" onClick={() => applyToAll({
            supports_photos: false,
            requires_photos: false,
            supports_comments: true,
            requires_comments: true,
          })}>
            All With Comments
          </Button>
          <Button variant="outline" size="sm" onClick={() => applyToAll({
            supports_photos: true,
            requires_photos: true,
            supports_comments: true,
            requires_comments: true,
            min_photos: 1,
          })}>
            All Full Documentation
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <ScrollArea className="h-[400px] border rounded-lg p-4">
            <div className="space-y-2">
              {configuredItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => setSelectedItemIndex(index)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedItemIndex === index 
                      ? 'bg-primary/10 border-primary' 
                      : 'bg-background hover:bg-muted/50 border-border'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.label}</p>
                      {item.description && (
                        <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                      )}
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {getItemRequirements(item).map((badge, idx) => (
                          <Badge key={idx} variant={badge.color as any} className="text-xs">
                            <badge.icon className="h-3 w-3 mr-1" />
                            {badge.text}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {item.type}
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>

          <div className="border rounded-lg p-4">
            {selectedItemIndex !== null ? (
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-sm mb-1">
                    {configuredItems[selectedItemIndex].label}
                  </h3>
                  {configuredItems[selectedItemIndex].description && (
                    <p className="text-xs text-muted-foreground">
                      {configuredItems[selectedItemIndex].description}
                    </p>
                  )}
                </div>

                <MediaRequirementsEditor
                  supports_photos={configuredItems[selectedItemIndex].supports_photos}
                  requires_photos={configuredItems[selectedItemIndex].requires_photos}
                  supports_comments={configuredItems[selectedItemIndex].supports_comments}
                  requires_comments={configuredItems[selectedItemIndex].requires_comments}
                  min_photos={configuredItems[selectedItemIndex].min_photos}
                  max_photos={configuredItems[selectedItemIndex].max_photos}
                  onUpdate={(updates) => updateItemMedia(selectedItemIndex, updates)}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                Select an item to configure
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Configuration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
